import React, { useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  AppAvatar,
  Col,
  Row,
  UiCenter,
  UiLink,
  UiPrimaryButton,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";
import { PAGES } from "~/constants";
import { JOB_STATUS_COMPLETED, JOB_STATUS_RESULT_MAP } from "~/constants/job";
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { createNewAllAccessJob, fetchMyListJob, fetchPendingJob, fetchQuota } from "~/redux/dashboards/thunk";
import { formatLongMonth } from "~/utils/helpers";
import { setConfirmKeyFeature, setConfirmKeyFeature2, setStyleData } from "~/redux/dashboards/slice";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { RefreshIcon } from "~/components/icons/SvgIcons";
import { requestOK } from "redux-thunk-kit";
import { useSelector } from "react-redux";
import { RootState } from "~/redux/root-reducer";
import isEmpty from "lodash/isEmpty";
import { fetchCurrentUserInfo } from "~/redux/users/thunk";

export function ViewResultPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const data = useAppSelector(state => state.dashboard.jobData);
  const quota = useAppSelector(state => state.dashboard.quota);
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const vrTitle = useBreakpointValue<any>({ base: "headline4", sm: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });

  const jobPendingData = useSelector((state: RootState) => state.dashboard.jobPendingData);

  const { loading: loading1 } = useRequestState(fetchMyListJob);
  const { loading: loading2 } = useRequestState(fetchPendingJob);
  const { loading: loading3 } = useRequestState(createNewAllAccessJob);
  const isLoading = loading1 || loading2 || loading3;

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchCurrentUserInfo({}));
      await dispatch(fetchMyListJob({ orderBy: "created_at,desc" }));
      await dispatch(fetchPendingJob({}));
      await dispatch(fetchQuota({}));
      dispatch(setStyleData(undefined));
      dispatch(setConfirmKeyFeature(null));
      dispatch(setConfirmKeyFeature2(null));
    };
    fetchData();
  }, [dispatch]);

  const handleOnClickResult = (itemId: string) => {
    history.push(`${PAGES.RESULT_DETAIL}/${itemId}`);
  };

  const resolveJobTitle = (item: any) => {
    const keyFeatures = item?.attributes?.key_features;
    if (Array.isArray(keyFeatures) && keyFeatures.length > 0) {
      const names = keyFeatures.map((feature: any) => feature?.name).filter(Boolean);
      if (names.length > 0) {
        return names.join(" & ");
      }
    }

    if (item?.user?.name) {
      return item.user.name;
    }

    return currentUser?.email;
  };
  const getNewHeadshotTitle = () => {
    if (!isEmpty(jobPendingData)) {
      return "Resume your last job";
    }
    if (currentUser?.is_all_access_plan) {
      if (Number(quota?.remaining_quota) <= 0) {
        return "You are out of quota";
      }
      return `Create a new photoshoot. You have ${quota?.remaining_quota} styles left`;
    }
    return "Create a new photoshoot";
  };

  const onClickCreateNewHeadshot = async () => {
    if (!isEmpty(jobPendingData)) {
      history.push(`${PAGES.DASHBOARD}`);
      return;
    }
    if (currentUser?.is_all_access_plan) {
      const resAction = await dispatch(createNewAllAccessJob({}));
      if (requestOK(resAction, createNewAllAccessJob)) {
        const resAction2 = await dispatch(fetchPendingJob({}));
        if (requestOK(resAction2, fetchPendingJob)) {
          history.push(`${PAGES.DASHBOARD}?is_new_hs=1`);
        }
      }
    } else {
      const resAction2 = await dispatch(fetchPendingJob({}));
      if (requestOK(resAction2, fetchPendingJob)) {
        history.push(`${PAGES.DASHBOARD}?is_new_hs=1`);
      }
    }
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Results`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
    >
      <Col flex={1} justify="center">
        <UiCenter p={4}>
          <DashboardMainBox>
            <UiText vr={vrTitle} textAlign="center">
              View your results
            </UiText>
            <UiText vr={vrCaption} mt={{ base: 2, sm: 3 }} textAlign="center">
              Click the &quot;Results&quot; button to view and download your results
            </UiText>
            <UiVStack mt={{ base: 4, sm: 8 }} gap={4} w="full" flexGrow={1}>
              {data?.map(item => (
                <Col key={item.id} p={4} bg="neutral.gray00" w="full" rounded="xl">
                  <Row w="full" align="center" position="relative">
                    <AppAvatar url={currentUser?.profile_picture} w="2.6rem" h="2.6rem" borderRadius={8} />
                    <Col flex={1} justify="space-between" ml={5} h="full">
                      <Row align="center">
                        <UiText vr="title2" textAlign="left">
                          {resolveJobTitle(item)}
                        </UiText>
                      </Row>
                      <Row align="center" gap={3} wrap="wrap">
                        <UiText vr="body2" textAlign="left" color="neutral.gray600">
                          {formatLongMonth(item.created_at) || "Created at ..."}
                        </UiText>
                      </Row>
                    </Col>
                    <Row gap={3}>
                      {item.status === JOB_STATUS_COMPLETED ? (
                        <UiPrimaryButton
                          h="2.5rem"
                          w={{ base: "fit-content", md: "9.25rem" }}
                          variant="outline"
                          borderColor={item.status === "completed" && !item.paid_at ? "darkblue" : "darkblue"}
                          bg="transparent"
                          color="darkblue"
                          _hover={{
                            bg: "transparent",
                            color: "darkblue",
                          }}
                          fontSize={{ base: "0.875rem", sm: "1rem" }}
                          onClick={() => handleOnClickResult(item.id)}
                        >
                          <UiText vr="title1" className={item.status === "completed" && !item.paid_at ? "" : ""}>
                            Results
                          </UiText>
                        </UiPrimaryButton>
                      ) : (
                        <Row align="center" h="2.5rem" px={5} bg="lightgreen" borderRadius="40px" color="green">
                          <RefreshIcon boxSize={4} color="inherit" mx={0} />
                          <UiText ml={1} vr="title1" fontSize={{ base: "0.875rem", sm: "1rem" }} color="inherit">
                            {JOB_STATUS_RESULT_MAP[item.status] || "Processing..."}
                          </UiText>
                        </Row>
                      )}
                    </Row>
                  </Row>
                  {item.has_upsell_pending && item.upsells.length > 0 && (
                    <Col align="flex-end" mt={2}>
                      <UiLink
                        as={Link}
                        to={`${PAGES.SELECT_STYLES}?job_id=${item.id}&type=upsell`}
                        color="neutral.gray600"
                        fontSize="14px"
                      >
                        {item.upsells
                          ?.filter(upsell => upsell.status === "pending")
                          .reduce((total, upsell) => total + upsell.max_outfit, 0)}{" "}
                        more styles available. Add now!
                      </UiLink>
                    </Col>
                  )}
                  {item.has_upsell_processing && (
                    <Col align="flex-end" mt={2}>
                      <UiLink color="neutral.gray600" fontSize="14px">
                        Processing your additional styles...
                      </UiLink>
                    </Col>
                  )}
                </Col>
              ))}
            </UiVStack>

            <UiPrimaryButton
              h="2.5rem"
              maxW={{ base: undefined, md: "32rem" }}
              w="full"
              variant="outline"
              borderColor="darkblue"
              bg="transparent"
              color="darkblue"
              mt="8"
              isLoading={isLoading}
              _hover={{
                bg: "transparent",
                color: "darkblue",
              }}
              _loading={{
                bg: "transparent",
                color: "darkblue",
              }}
              onClick={onClickCreateNewHeadshot}
            >
              {getNewHeadshotTitle()}
            </UiPrimaryButton>
          </DashboardMainBox>
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
